import { type MatchFunction, type MatchResult, match } from 'path-to-regexp';

import { NavigationRevisionCurrent } from './schema';
import type { NavigationRevision } from './types';

export type PathMatch<
    Params extends { [K in keyof Params]?: string } = { [key: string]: string | undefined },
> = MatchResult<Params>;

const matchPathCache = new Map<string, MatchFunction<{ [key: string]: string }>>();

/**
 * Normalize the basepath
 */
export function normalizePath(basepath = ''): string {
    basepath = basepath[basepath.length - 1] === '/' ? basepath.slice(0, -1) : basepath;
    basepath = basepath[0] === '/' ? basepath : `/${basepath}`;
    return basepath;
}

/*
 * Runs the first matching route for given pathname,
 * like a functional <ReactRouter.Switch>
 */
export function switcher<Result>(
    pathname: string,
    routes: Array<{
        path: string;
        exact?: boolean;
        onMatch: (match: PathMatch<any>) => Result;
    }>,
    options: { prefix?: string } = {}
): Result | undefined {
    let result: Result | undefined;

    let { prefix = '' } = options;
    if (prefix === '/') {
        prefix = '';
    }

    if (!pathname.startsWith(prefix)) {
        return;
    }

    pathname = pathname.slice(prefix.length);
    routes.some((routeDef) => {
        const cacheKey = `${Boolean(routeDef.exact)}${routeDef.path}`;
        const routePath = routeDef.path;
        const pathMatch =
            matchPathCache.get(cacheKey) ||
            match(routePath, {
                decode: decodeURIComponent,
                start: true,
                end: Boolean(routeDef.exact),
                sensitive: false,
                strict: false,
            });
        matchPathCache.set(cacheKey, pathMatch);

        const matchResult = pathMatch(pathname);
        if (matchResult) {
            result = routeDef.onMatch({
                ...matchResult,
                path: `${prefix}${matchResult.path}`,
            });
            return true;
        }
    });

    return result;
}

export function formatRevision(revision: NavigationRevision): string {
    return revision === NavigationRevisionCurrent ? 'current' : revision;
}

export function parseRevision(revision: string): NavigationRevision {
    return revision === 'current' ? NavigationRevisionCurrent : revision;
}

/**
 * Return whether a navigation revision points to a persisted revision id.
 */
export function isNavigationRevisionId(
    revision: NavigationRevision | undefined
): revision is string {
    return typeof revision === 'string';
}
